import { StandardBreadcrumbs } from '@components/Breadcrumbs/StandardBreadcrumbs';
import { StyledButton } from '@components/Button';
import { LoadingSpinner } from '@components/Loading';
import { FullscreenLoadingSpinner } from '@components/Loading/FullscreenLoadingSpinner';
import { Toast } from '@components/Toast';
import { device } from '@constants/mediaQueries';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useTranslation from '@hooks/useTranslation';
import { Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import MUITypography from '@mui/material/Typography';
import { ExportButton } from '@pages/OrdersPages/ExportButton';
import { CompletedButton } from '@pages/OrdersPages/styles';
import { useTenant } from '@providers/Tenant';
import { formatDateWithTime } from '@services/formatDate';
import OrderPartsList from './OrderPartsList';
import {
  ExportButtonContainer,
  OrderActionsContainer,
  OrderDetailsButtonsContainer,
  OrderDetailsData,
  OrderDetailsDataCell,
  OrderDetailsDataHeader,
  OrderDetailsDataRow,
  OrderDetailsDivider,
  OrderDetailsHeader,
  OrderDetailsMainContent,
  OrderDetailsPanel,
  OrderDetailsPanelTitle,
  OrderDetailsSection,
  OrderDetailsTitleContainer,
  OrderItemsPanel,
} from './styles';
import { useOrderDetails } from './useOrderDetails';

type OrderDetailsProps = {
  variant: 'received' | 'myOrders';
};

export function OrderDetails({ variant }: Readonly<OrderDetailsProps>) {
  const {
    placeHolderOrder,
    markAsCompleted,
    sendMailToSupplier,
    sendMailToCustomer,
    isLoadingOrderDetails,
    isLoadingCompletedOrder,
    isLoadingResendEmailToSupplier,
    isLoadingResendEmailToCustomer,
    isErrorCompletedOrder,
    isSuccessResendEmailToSupplier,
    isSuccessResendEmailToCustomer,
    isErrorOrderDetails,
    isErrorResendEmailToSupplier,
    isErrorResendEmailToCustomer,
    orderDetails,
  } = useOrderDetails();

  const isErrorResendingEmail = isErrorResendEmailToSupplier || isErrorResendEmailToCustomer;
  const isEmailResendingSuccessful = isSuccessResendEmailToSupplier || isSuccessResendEmailToCustomer;
  const isLoadingResendEmail = isLoadingResendEmailToSupplier || isLoadingResendEmailToCustomer;

  const isReceivedOrders = variant === 'received';
  const isMyOrders = variant === 'myOrders';

  const order = orderDetails ?? placeHolderOrder;

  const {
    orderId,
    eCommerceOrderName,
    firstName,
    lastName,
    companyName,
    telephone,
    items,
    createdByEmail: email,
    timestamp,
    status,
    totalPrice,
  } = order;

  const { country, city, address1, address2, postalCode } = order.address;

  const customerAccountFieldValue = order['vtaas-ui:checkout:customerAccountField'];
  const customerAccountFieldLabel = `${useTranslation(
    'vtaas-ui:checkout:customerAccountField',
    `${isMyOrders ? /* istanbul ignore next */ 'Your' : 'Customer'} account`
  )}:`;

  const customerReferenceFieldValue = order['vtaas-ui:checkout:customerReferenceField'];
  const customerReferenceFieldLabel = `${useTranslation(
    'vtaas-ui:checkout:customerReferenceField',
    `${isMyOrders ? /* istanbul ignore next */ 'Your' : 'Customer'} ref`
  )}:`;

  const messageToSellerFieldValue = order['vtaas-ui:checkout:messageToSellerField'];
  const messageToSellerFieldLabel = `${useTranslation(
    'vtaas-ui:checkout:messageToSellerField',
    isMyOrders ? /* istanbul ignore next */ 'Message' : 'Message from customer'
  )}:`;

  const isMobile = useMediaQuery(device.mobileOrTablet);
  const { enabledFeatures } = useTenant();
  const isShopifyEnabledAccount = enabledFeatures.includes('ecommerce');

  const actionButtonDividerOrientation = useMediaQuery(useTheme().breakpoints.up('sm'))
    ? /* istanbul ignore next */ 'vertical'
    : 'horizontal';

  if (isLoadingOrderDetails) {
    return (
      <>
        <FullscreenLoadingSpinner />
        <Toast
          dependency={isErrorOrderDetails}
          severity="error"
          title="Error"
          message="There has been an error loading this order"
        />
        <Toast
          dependency={isErrorCompletedOrder}
          severity="error"
          title="Error"
          message="There has been an error completing this order"
        />
        <Toast dependency={isEmailResendingSuccessful} severity="success" title="Success" message="Email resent!" />
        <Toast
          dependency={isErrorResendingEmail}
          severity="error"
          title="Error"
          message="There has been an error resending the email!"
        />
      </>
    );
  }

  return (
    <>
      {!isMobile && (
        <StandardBreadcrumbs
          current={{ name: eCommerceOrderName ?? orderId }}
          parents={[
            { name: 'My account', url: '/my-account' },
            {
              id: `${isMyOrders ? /* istanbul ignore next */ 'My' : 'Received'} orders`,
              name: `${isMyOrders ? /* istanbul ignore next */ 'My' : 'Received'} orders`,
              url: `/${isMyOrders ? /* istanbul ignore next */ 'my' : 'received'}-orders`,
            },
          ]}
          sx={{ margin: '1rem 0 2rem 0' }}
        />
      )}
      <OrderDetailsHeader>
        <OrderDetailsTitleContainer>
          <MUITypography variant="h5">
            Order details for {isMobile ? /* istanbul ignore next */ orderId : eCommerceOrderName ?? orderId}
          </MUITypography>
          <MUITypography variant="body1">
            Order {isMyOrders ? /* istanbul ignore next */ 'submitted' : 'received'}: {formatDateWithTime(timestamp)}{' '}
          </MUITypography>
        </OrderDetailsTitleContainer>
        {!isShopifyEnabledAccount && (
          <OrderActionsContainer>
            <ExportButtonContainer>
              <Typography>Export order to:</Typography>
              <ExportButton type="pdf" order={order} />
              <ExportButton type="csv" orders={[order]} />
            </ExportButtonContainer>
            <OrderDetailsButtonsContainer>
              <StyledButton
                variant="outlined"
                sx={{ marginRight: 'unset' }}
                startIcon={
                  isLoadingResendEmail ? /* istanbul ignore next */ null : <FontAwesomeIcon icon={faEnvelope} />
                }
                onClick={() =>
                  isMyOrders ? /* istanbul ignore next */ sendMailToCustomer(orderId) : sendMailToSupplier(orderId)
                }
              >
                {isLoadingResendEmail ? (
                  /* istanbul ignore next */ <LoadingSpinner width="100" height="100" />
                ) : (
                  'Resend me the order'
                )}
              </StyledButton>
              {isReceivedOrders && (
                <>
                  <Divider orientation={actionButtonDividerOrientation} flexItem />
                  <CompletedButton
                    orderStatus={status}
                    onClick={() => markAsCompleted(orderId)}
                    loading={isLoadingCompletedOrder}
                    data-testid="completed-button"
                  />
                </>
              )}
            </OrderDetailsButtonsContainer>
          </OrderActionsContainer>
        )}
      </OrderDetailsHeader>
      <OrderDetailsMainContent>
        <OrderItemsPanel>
          <OrderDetailsSection sx={{ height: '100%' }}>
            <OrderDetailsPanelTitle>Parts ordered</OrderDetailsPanelTitle>
            {items.length !== 0 && !isErrorOrderDetails && <OrderPartsList items={items} totalPrice={totalPrice} />}
          </OrderDetailsSection>
        </OrderItemsPanel>
        <OrderDetailsPanel>
          <OrderDetailsSection>
            <OrderDetailsPanelTitle>Customer details</OrderDetailsPanelTitle>
            <OrderDetailsData>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>First name:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{firstName}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Last name:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{lastName}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Company name:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{companyName}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Telephone:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{telephone}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Email:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{email}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
            </OrderDetailsData>
          </OrderDetailsSection>
          <OrderDetailsDivider />
          <OrderDetailsSection>
            <OrderDetailsPanelTitle>Delivery address</OrderDetailsPanelTitle>
            <OrderDetailsData>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Building name / number:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{address1}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Street address:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{address2}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Town / City:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{city}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Postcode:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{postalCode}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>Country:</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{country}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
            </OrderDetailsData>
          </OrderDetailsSection>
          <OrderDetailsDivider />
          <OrderDetailsSection>
            <OrderDetailsData>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>{customerReferenceFieldLabel}</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{customerReferenceFieldValue}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>{customerAccountFieldLabel}</OrderDetailsDataHeader>
                <OrderDetailsDataCell>{customerAccountFieldValue}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
              <OrderDetailsDataRow>
                <OrderDetailsDataHeader>{messageToSellerFieldLabel}</OrderDetailsDataHeader>
                <OrderDetailsDataCell sx={{ fontWeight: 'normal' }}>{messageToSellerFieldValue}</OrderDetailsDataCell>
              </OrderDetailsDataRow>
            </OrderDetailsData>
          </OrderDetailsSection>
        </OrderDetailsPanel>
      </OrderDetailsMainContent>

      <Toast
        dependency={isErrorOrderDetails}
        severity="error"
        title="Error"
        message="There has been an error loading this order"
      />
      <Toast
        dependency={isErrorCompletedOrder}
        severity="error"
        title="Error"
        message="There has been an error completing this order"
      />
      <Toast dependency={isEmailResendingSuccessful} severity="success" title="Success" message="Email resent!" />
      <Toast
        dependency={isErrorResendingEmail}
        severity="error"
        title="Error"
        message="There has been an error resending the email!"
      />
    </>
  );
}
